// @ai-generated 2026-09-04T08:07:37+00:00 by dimitri.driediger@synqony.com using Claude Opus 5
import { type ApiLocationSnapshot } from '@Model';
import {
    ActionTile,
    type BadgeProps,
    ConfirmationModal,
    EnumMessageLevel,
    FieldWithError,
    FlexColumn,
    iconSizeMd,
    joinClassNames,
    useTranslate,
    useWizard,
    useWizardErrors,
    WizardStepTitle,
} from '@synqo/frontend-component-library';
import { IconArrowsExchange, IconPackage, IconRefresh, IconTruckDelivery, IconTruckReturn } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { actionSwitchMessage } from '../../containerOrder';
import { actionTileTitle, picksFromStock, type RequisitionAction } from '@utils/requisitionAction';
import { type RequisitionWizardState } from '../../RequisitionWizardState';
import classes from '../ContainerSteps.module.css';
import { SiteCard } from '../SiteCard';

export type ActionStepErrorKey = 'action';

interface ActionTileDefinition {
    action: RequisitionAction;
    description: string;
    icon: ReactNode;
    /** Spans both columns, which the loose-material tile does as the odd one out. */
    fullWidth?: boolean;
}

const ACTION_TILES: ActionTileDefinition[] = [
    {
        action: 'placement',
        description: 'Ein neuer Container kommt zu dir.',
        icon: <IconTruckDelivery size={iconSizeMd} />,
    },
    {
        action: 'pickup',
        description: 'Ein Container geht weg.',
        icon: <IconTruckReturn size={iconSizeMd} />,
    },
    {
        action: 'emptying',
        description: 'Wird geleert und bleibt stehen.',
        icon: <IconRefresh size={iconSizeMd} />,
    },
    {
        action: 'swap',
        description: 'Voller raus, leerer rein.',
        icon: <IconArrowsExchange size={iconSizeMd} />,
    },
    {
        action: 'material',
        description: 'Ohne Behälter — z. B. 5 t Altholz.',
        icon: <IconPackage size={iconSizeMd} />,
        fullWidth: true,
    },
];

interface ActionStepBodyProps {
    site: ApiLocationSnapshot;
    /** How many containers stand at the site, as far as our records go. */
    stockTotal: number;
    /** How many containers open orders plan to place at the site. */
    plannedTotal: number;
    /** Whether the stock is still on its way, which keeps the pill off the tiles that pick from it. */
    isStockLoading: boolean;
    action: RequisitionAction | undefined;
    /**
     * How much of the order would be lost by switching to another action: the one container of a
     * container order, or the positions of a loose-material one. Either kind is discarded, so
     * either kind has to be asked about.
     */
    discardableCount: number;
    /** Takes the action; the step moves on by itself. */
    onChoose: (action: RequisitionAction) => void;
}

/**
 * Step 2: the one question that decides the rest of the order.
 *
 * Choosing a tile is the step's "next" - there is no separate button, because the tile already
 * said what happens. Switching away from an action that has positions asks first, since they
 * only mean anything under the action they were entered for.
 */
export function ActionStepBody({
    site,
    stockTotal,
    plannedTotal,
    isStockLoading,
    action,
    discardableCount,
    onChoose,
}: ActionStepBodyProps) {
    const i18n = useTranslate('');
    const errors = useWizardErrors<ActionStepErrorKey>();
    const wizard = useWizard<RequisitionWizardState>();
    const [pendingAction, setPendingAction] = useState<RequisitionAction | undefined>(undefined);
    const [isAdvancing, setIsAdvancing] = useState(false);

    /**
     * Takes the action and asks to move on.
     *
     * The move waits for the next render: the step's validation reads the action the tile just
     * set, and only sees it once it has arrived as a prop. Advancing right here would fail that
     * validation, and the wizard refuses a forward jump that skips it.
     */
    const take = useCallback(
        (chosen: RequisitionAction) => {
            onChoose(chosen);
            setIsAdvancing(true);
        },
        [onChoose],
    );

    useEffect(() => {
        if (!isAdvancing || action === undefined) {
            return;
        }
        setIsAdvancing(false);
        void wizard.goNext();
    }, [isAdvancing, action, wizard]);

    const requestChoose = useCallback(
        (chosen: RequisitionAction) => {
            if (chosen !== action && discardableCount > 0) {
                setPendingAction(chosen);
                return;
            }
            take(chosen);
        },
        [action, discardableCount, take],
    );

    const confirmSwitch = useCallback(() => {
        if (pendingAction) {
            take(pendingAction);
        }
        setPendingAction(undefined);
    }, [pendingAction, take]);

    const stockBadge = stockPill(stockTotal, plannedTotal, isStockLoading, i18n);

    return (
        <>
            <WizardStepTitle
                title={i18n('Was möchtest du tun?')}
                subtitle={i18n('Eine Bestellung, eine Sache — den Rest fragen wir danach.')}
            />

            <FlexColumn align="stretch" gap="md">
                <SiteCard site={site} onChange={() => wizard.goTo(0)} />

                <FieldWithError error={action ? undefined : errors.action}>
                    <div className={classes.tileGrid}>
                        {ACTION_TILES.map((tile) => (
                            <ActionTile
                                key={tile.action}
                                className={joinClassNames(
                                    classes.actionTile,
                                    tile.fullWidth ? classes.tileFullWidth : '',
                                    tile.action === action ? classes.tileChosen : '',
                                )}
                                icon={tile.icon}
                                title={i18n(actionTileTitle(tile.action))}
                                description={i18n(tile.description)}
                                badge={picksFromStock(tile.action) || tile.action === 'swap' ? stockBadge : undefined}
                                onClick={() => requestChoose(tile.action)}
                            />
                        ))}
                    </div>
                </FieldWithError>
            </FlexColumn>

            {/* Information rather than Warning: switching is a normal step, and Warning paints the
                confirmation as the destructive red button the specification does not ask for. */}
            <ConfirmationModal
                isOpen={pendingAction !== undefined}
                title={i18n('Aktion wechseln?')}
                message={actionSwitchMessage(discardableCount, action, pendingAction)}
                messageLevel={EnumMessageLevel.Information}
                modalButtonContent={i18n('Aktion wechseln')}
                onConfirm={confirmSwitch}
                onCancel={() => setPendingAction(undefined)}
            />
        </>
    );
}

/**
 * The pill on the tiles that pick a container off the stock.
 *
 * Names the standing and the planned containers; a site with neither says so rather than showing a
 * zero. The tile stays open: the customer may well know of a container we do not.
 */
// @ai-generated 2026-09-25T12:26:13+00:00 by matthias.gessinger@synqony.com using Claude Opus 5.5 (1M context)
function stockPill(
    stockTotal: number,
    plannedTotal: number,
    isStockLoading: boolean,
    i18n: (key: string) => string,
): BadgeProps | undefined {
    if (isStockLoading) {
        return undefined;
    }

    if (stockTotal === 0 && plannedTotal > 0) {
        return { children: `${plannedTotal} ${i18n('Container geplant')}`, variant: 'primary' };
    }

    if (stockTotal === 0) {
        return { children: i18n('Kein Container erfasst'), variant: 'default' };
    }

    const standing = `${stockTotal} ${i18n('Container vor Ort')}`;
    return {
        children: plannedTotal > 0 ? `${standing} · ${plannedTotal} ${i18n('geplant')}` : standing,
        variant: 'primary',
    };
}
